import type { League } from '@h2h/shared';
import { ArrowRight, Check, Clock } from '../../components/ui/icons';
import { NationalFlagEmblem } from '../../components/ui/NationalFlagEmblems';
import {
  useCurrentRound,
  useMyEntry,
  useJoinLeague
} from '../../hooks/useQueries';
import { FOCUSED_LEAGUE_ID, identityFor } from '../../data/leagues';
import { countdownTo } from '../../lib/format';

export function WorldCupCard({
  league,
  joined,
  uid,
  unread,
  onPlay
}: {
  league: League | undefined;
  joined: boolean;
  uid: string | null;
  unread: number;
  onPlay: () => void;
}) {
  const roundId = league?.currentRoundId ?? null;
  const { data: round } = useCurrentRound(league?.leagueId, roundId);
  const { data: entry } = useMyEntry(league?.leagueId, roundId, uid);
  const join = useJoinLeague(uid);
  const name = league?.name ?? identityFor(FOCUSED_LEAGUE_ID)?.name ?? 'Football Tournament';

  const settled = round?.status === 'SETTLED';
  const open = round?.status === 'OPEN' || round?.status === 'LOCKING';
  const live = round?.status === 'LOCKING' || round?.status === 'SETTLING';
  const needsPicks = open && !entry;

  const footer = !joined
    ? 'Join to build your first cards'
    : settled
      ? 'Round settled · see results'
      : needsPicks
        ? round?.pickMode === 'FLEXIBLE'
          ? 'Build your cards · locks in '
          : `${round?.picksRequired ?? 3} cards · locks in `
        : open
          ? round?.pickMode === 'FLEXIBLE'
            ? 'Cards in · editable per game until kickoff'
            : 'Cards in · editable until first kickoff'
          : entry
            ? 'Cards locked · points update live'
            : 'Next match-day opens soon';

  const cta = !joined ? 'Join' : settled ? 'Results' : needsPicks ? 'Build' : 'Open';

  const handle = () => {
    if (!joined && !join.isPending) {
      join.mutate(FOCUSED_LEAGUE_ID, { onSuccess: onPlay });
      return;
    }
    onPlay();
  };

  return (
    <div className="overflow-hidden rounded-[22px] border-[1.5px] border-night shadow-[0_14px_30px_-18px_rgba(0,0,0,0.3)]">
      <div className="flex items-center gap-3 bg-[radial-gradient(120%_120%_at_85%_-20%,#16271F,#0C0D14)] p-[15px_16px]">
        <NationalFlagEmblem leagueId={FOCUSED_LEAGUE_ID} size="md" />
        <div className="flex-1">
          <div className="text-[16px] font-extrabold tracking-[-0.01em] text-white">{name}</div>
          <div className="mt-0.5 text-[12px] font-medium text-white/55">
            {round?.label ?? 'Group stage'} · World
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent/16 px-[9px] py-[5px] text-[11px] font-extrabold text-accent">
              <span className="h-[6px] w-[6px] rounded-full bg-accent" />
              {unread} new
            </span>
          )}
          {live ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#24C56A]/40 bg-[#24C56A]/16 px-[10px] py-[5px]">
              <span className="h-[6px] w-[6px] animate-h2h-pulse rounded-full bg-accent" />
              <span className="text-[11px] font-extrabold tracking-[0.04em] text-accent">LIVE</span>
            </span>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        onClick={handle}
        disabled={join.isPending}
        className="tap flex w-full items-center justify-between gap-3 bg-night px-[16px] py-[13px] text-white disabled:opacity-70"
      >
        <span className="flex items-center gap-2 text-[12px] font-semibold text-white/85">
          {entry && !needsPicks && !settled ? (
            <Check width={14} height={14} strokeWidth={2.6} className="text-accent" />
          ) : needsPicks && round ? (
            <Clock width={14} height={14} strokeWidth={2.4} className="text-[#FF8E89]" />
          ) : null}
          <span>
            {footer}
            {needsPicks && round ? (
              <span className="num font-extrabold text-[#FF8E89]">{countdownTo(round.firstKickoffAt)}</span>
            ) : null}
          </span>
        </span>
        <span className="flex items-center gap-1.5 rounded-full bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] px-4 py-[7px] text-[12px] font-extrabold">
          {join.isPending ? 'Joining…' : cta} <ArrowRight width={13} height={13} strokeWidth={2.8} />
        </span>
      </button>
    </div>
  );
}
